import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { logger } from '@/utils/logger';
import { useTranslation } from 'react-i18next';
import { CSVUploader } from '../components/CSVUploader';
import Modal from '../components/ui/Modal';
import { getContentApiUrl } from '@/config/api.config';

// Home content types
interface HomeContent {
  hero_title: string;
  hero_subtitle: string;
  hero_image: string;
  hero_cta_text: string;
  hero_cta_link: string;
  section1_title: string;
  section1_content: string;
}

// Types matching frontend data structures
interface Programme {
  id: string;
  icon: 'UserIcon' | 'UserGroupIcon' | 'GlobeAltIcon' | 'HeartIcon' | 'WrenchIcon';
  image: string;
  title: string;
  subtitle?: string;
  description: string;
  features: string[];
  callout?: string;
}

interface LocaleText {
  en: string;
  zhCN: string;
}

const FIXED_MARKETING_PLACEMENTS = ['home_gallery', 'home_popup'] as const;
type MarketingPlacementKey = (typeof FIXED_MARKETING_PLACEMENTS)[number];

interface MarketingPlacement {
  key: MarketingPlacementKey;
  enabled: boolean;
  order: number;
}

interface MarketingAsset {
  desktopUrl: string;
  mobileUrl?: string;
  localeDesktopUrls?: {
    en?: string;
    zhCN?: string;
  };
  alt: LocaleText;
}

interface MarketingCampaign {
  id: string;
  slug: string;
  type: 'training_camp' | 'event' | 'promotion';
  status: 'draft' | 'scheduled' | 'live' | 'archived';
  startDate: string;
  endDate: string;
  priority: number;
  placements: MarketingPlacement[];
  content: {
    campaignName: LocaleText;
    tagline: LocaleText;
    summary: LocaleText;
    stageLabel?: LocaleText;
    trainingFocus?: LocaleText;
    scheduleText?: LocaleText;
  };
  contactOverlay?: {
    addressLine1: string;
    addressLine2: string;
    phone: string;
    email?: string;
  };
  cta: {
    href: string;
    label: LocaleText;
    openInNewTab?: boolean;
  };
  assets: {
    gallery: MarketingAsset;
    popup?: MarketingAsset;
  };
  popupRule?: {
    enabled: boolean;
    version: string;
    frequency?: 'daily_once';
    timezone?: 'Pacific/Auckland';
    campaignAssetKey?: 'popup';
  };
}

interface MarketingCampaignsPayload {
  updatedAt?: string | null;
  campaigns: MarketingCampaign[];
}

interface Article {
  id: string;
  title: string;
  slug: string;
  date: string;
  excerpt: string;
  content: string;
  tags: string[];
  featuredImage?: {
    sourceUrl: string;
  };
}

interface EventItem {
  id: string;
  title: string;
  date: string;
  location: string;
  event_level?: string;
  event_status: 'UPCOMING' | 'PAST';
  categories: string[];
  categories_display?: string;
  link?: string;
  image_url?: string;
  end_date?: string;
}

interface Coach {
  id: string;
  name: string;
  title: string;
  bio: string;
  image: string;
  specialties: string[];
  experience: string;
  hourlyRate: number;
  email: string;
}

interface CoachingContent {
  page_title: string;
  page_subtitle: string;
  philosophy_title: string;
  philosophy_content: string[];
}

interface RankingItem {
  id: string;
  player_name: string;
  ranking: number;
  points: number;
  category: string;
  country?: string;
  change?: number;
}

type ContentTab = 'home' | 'rankings' | 'events' | 'articles' | 'coaching' | 'programme' | 'marketing';
type ContentManagementMode = 'default' | 'programmeOnly' | 'marketingOnly';

interface ContentManagementProps {
  mode?: ContentManagementMode;
  embedded?: boolean;
}

const MODE_LOCKED_TAB: Record<Exclude<ContentManagementMode, 'default'>, ContentTab> = {
  programmeOnly: 'programme',
  marketingOnly: 'marketing',
};

const QUERY_SYNCABLE_TABS = new Set<ContentTab>(['home', 'articles', 'events', 'coaching', 'rankings']);

const resolveInitialTabByMode = (mode: ContentManagementMode): ContentTab => {
  if (mode === 'default') return 'home';
  return MODE_LOCKED_TAB[mode];
};

const ContentManagement: React.FC<ContentManagementProps> = ({ mode = 'default', embedded = false }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab: ContentTab = resolveInitialTabByMode(mode);
  const [activeTab, setActiveTab] = useState<ContentTab>(initialTab);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Home state
  const [homeContent, setHomeContent] = useState<HomeContent>({
    hero_title: "New Zealand's Premier Badminton Centre",
    hero_subtitle: "Six international-standard courts in Auckland\nProfessional coaching • Tournament hosting • Equipment sales",
    hero_image: "https://images.unsplash.com/photo-1626224583764-f87db24ac4ea",
    hero_cta_text: "Book Court",
    hero_cta_link: "/booking",
    section1_title: "Badminton Excellence",
    section1_content: "From beginner lessons to competitive tournaments, we offer comprehensive badminton services for all skill levels"
  });

  // Rankings state
  const [rankings, setRankings] = useState<RankingItem[]>([]);
  const [rankingCategory, setRankingCategory] = useState("Men's Singles");

  // Events state
  const [events, setEvents] = useState<EventItem[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [eventForm, setEventForm] = useState<Partial<EventItem>>({
    categories: [],
    event_status: 'UPCOMING'
  });
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);

  // Articles state
  const [articles, setArticles] = useState<Article[]>([]);
  const [articleForm, setArticleForm] = useState<Partial<Article>>({
    tags: []
  });
  const [editingArticleId, setEditingArticleId] = useState<string | null>(null);
  const [isArticleModalOpen, setIsArticleModalOpen] = useState(false);

  // Coaching state
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [coachForm, setCoachForm] = useState<Partial<Coach>>({
    specialties: []
  });
  const [editingCoachId, setEditingCoachId] = useState<string | null>(null);
  const [isCoachModalOpen, setIsCoachModalOpen] = useState(false);
  const [coachingContent, setCoachingContent] = useState<CoachingContent>({
    page_title: 'Coaching',
    page_subtitle: 'Expert badminton coaches for all levels',
    philosophy_title: 'Our Coaching Philosophy',
    philosophy_content: [
      'We believe in developing complete badminton players.',
      'Our approach focuses on technical skills, physical fitness, and mental resilience.'
    ]
  });

  // Programme state
  const [programmes, setProgrammes] = useState<Programme[]>([]);
  const [programmeForm, setProgrammeForm] = useState<Partial<Programme>>({
    features: []
  });
  const [editingProgrammeId, setEditingProgrammeId] = useState<string | null>(null);
  const [isProgrammeModalOpen, setIsProgrammeModalOpen] = useState(false);
  const [marketingCampaigns, setMarketingCampaigns] = useState<MarketingCampaign[]>([]);
  const [marketingForm, setMarketingForm] = useState<Partial<MarketingCampaign>>({});
  const [editingMarketingId, setEditingMarketingId] = useState<string | null>(null);
  const [isMarketingModalOpen, setIsMarketingModalOpen] = useState(false);
  const [marketingStatusFilter, setMarketingStatusFilter] = useState<'all' | MarketingCampaign['status']>('all');
  const [marketingTypeFilter, setMarketingTypeFilter] = useState<'all' | MarketingCampaign['type']>('all');

  const iconOptions = [
    { value: 'UserIcon', label: t('content.programme.icons.user') },
    { value: 'UserGroupIcon', label: t('content.programme.icons.group') },
    { value: 'GlobeAltIcon', label: t('content.programme.icons.globe') },
    { value: 'HeartIcon', label: t('content.programme.icons.heart') },
    { value: 'WrenchIcon', label: t('content.programme.icons.wrench') }
  ];
  const marketingPlacementOptions: MarketingPlacementKey[] = [...FIXED_MARKETING_PLACEMENTS];

  const createEmptyLocaleText = (): LocaleText => ({ en: '', zhCN: '' });
  const normalizeMarketingPlacements = (
    placements?: Partial<MarketingPlacement>[]
  ): MarketingPlacement[] => FIXED_MARKETING_PLACEMENTS.map((placementKey, index) => {
    const existing = placements?.find((item) => item?.key === placementKey);
    return {
      key: placementKey,
      enabled: Boolean(existing?.enabled),
      order: Number(existing?.order || (index + 1)),
    };
  });

  const ensureMarketingContent = (
    content?: Partial<MarketingCampaign['content']>
  ): MarketingCampaign['content'] => ({
    campaignName: content?.campaignName || createEmptyLocaleText(),
    tagline: content?.tagline || createEmptyLocaleText(),
    summary: content?.summary || createEmptyLocaleText(),
    stageLabel: content?.stageLabel || createEmptyLocaleText(),
    trainingFocus: content?.trainingFocus || createEmptyLocaleText(),
    scheduleText: content?.scheduleText || createEmptyLocaleText(),
  });

  const buildPlacementCtaByTag = (tag?: string): { href: string; en: string; zhCN: string } => {
    const normalized = String(tag || '').trim().toLowerCase();
    if (!normalized) {
      return { href: '/programme', en: 'View Programme', zhCN: '查看课程' };
    }
    if (/(private|1v1|one-on-one|私教)/.test(normalized)) {
      return { href: '/programme?tab=private', en: 'Book Private Lesson', zhCN: '预约私教' };
    }
    if (/(junior|youth|kids|青少年)/.test(normalized)) {
      return { href: '/programme?tag=junior', en: 'View Junior Programme', zhCN: '查看青少年课程' };
    }
    if (/(beginner|starter|入门|初学)/.test(normalized)) {
      return { href: '/programme?tag=beginner', en: 'Join Beginner Camp', zhCN: '报名入门训练营' };
    }
    if (/(advanced|elite|进阶|高级)/.test(normalized)) {
      return { href: '/programme?tag=advanced', en: 'View Advanced Camp', zhCN: '查看进阶训练营' };
    }
    return { href: `/programme?tag=${encodeURIComponent(normalized)}`, en: 'View Programme', zhCN: '查看课程' };
  };

  const createEmptyMarketingCampaign = (): MarketingCampaign => ({
    id: '',
    slug: '',
    type: 'training_camp',
    status: 'draft',
    startDate: '',
    endDate: '',
    priority: 10,
    placements: normalizeMarketingPlacements([
      { key: 'home_gallery', enabled: true, order: 1 },
      { key: 'home_popup', enabled: false, order: 1 },
    ]),
    content: {
      campaignName: createEmptyLocaleText(),
      tagline: createEmptyLocaleText(),
      summary: createEmptyLocaleText(),
      stageLabel: createEmptyLocaleText(),
      trainingFocus: createEmptyLocaleText(),
      scheduleText: createEmptyLocaleText(),
    },
    contactOverlay: {
      addressLine1: '',
      addressLine2: '',
      phone: '',
      email: ''
    },
    cta: {
      href: '/programme',
      label: {
        en: 'View Programme',
        zhCN: '查看课程',
      },
      openInNewTab: false,
    },
    assets: {
      gallery: {
        desktopUrl: '',
        mobileUrl: '',
        localeDesktopUrls: {},
        alt: createEmptyLocaleText(),
      }
    },
    popupRule: {
      enabled: false,
      version: '',
      frequency: 'daily_once',
      timezone: 'Pacific/Auckland',
      campaignAssetKey: 'popup',
    },
  });

  const buildAuthorizedHeaders = (headers?: HeadersInit, asJson = false): Headers => {
    const merged = new Headers(headers || {});
    if (asJson && !merged.has('Content-Type')) {
      merged.set('Content-Type', 'application/json');
    }
    if (typeof window !== 'undefined') {
      const staffToken = localStorage.getItem('staff_token');
      if (staffToken) {
        merged.set('X-Staff-Token', staffToken);
        merged.set('Authorization', `Bearer ${staffToken}`);
      }
    }
    return merged;
  };

  const authorizedContentFetch = (url: string, init?: RequestInit, asJson = false) => {
    return fetch(url, {
      ...init,
      headers: buildAuthorizedHeaders(init?.headers, asJson),
    });
  };

  useEffect(() => {
    const lockedTab = mode === 'default' ? null : MODE_LOCKED_TAB[mode];
    if (lockedTab) {
      if (activeTab !== lockedTab) {
        setActiveTab(lockedTab);
      }
      return;
    }

    const queryTab = searchParams.get('tab');
    if (queryTab && QUERY_SYNCABLE_TABS.has(queryTab as ContentTab) && queryTab !== activeTab) {
      setActiveTab(queryTab as ContentTab);
    }
  }, [searchParams, activeTab, mode]);

  const handleTabChange = (nextTab: typeof activeTab) => {
    if (mode !== 'default') {
      setActiveTab(nextTab);
      return;
    }
    setActiveTab(nextTab);
    const nextSearch = new URLSearchParams(searchParams);
    if (nextTab === 'home') {
      nextSearch.delete('tab');
    } else {
      nextSearch.set('tab', nextTab);
    }
    setSearchParams(nextSearch, { replace: true });
  };

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      // Load data from API
      const [programmesRes, articlesRes, eventsRes, coachesRes, rankingsRes, homeRes, marketingRes] = await Promise.all([
        fetch(getContentApiUrl('programmes')),
        fetch(getContentApiUrl('articles')),
        fetch(getContentApiUrl('events')),
        fetch(getContentApiUrl('coaches')),
        fetch(getContentApiUrl('rankings')),
        fetch(getContentApiUrl('home')),
        fetch(getContentApiUrl('marketing-campaigns'))
      ]);

      if (programmesRes.ok) {
        const data = await programmesRes.json();
        setProgrammes(data);
      }
      if (articlesRes.ok) {
        const data = await articlesRes.json();
        setArticles(data);
      }
      if (eventsRes.ok) {
        const data = await eventsRes.json();
        setEvents(data);
      }
      if (coachesRes.ok) {
        const data = await coachesRes.json();
        setCoaches(data.coaches || []);
        if (data.content) {
          setCoachingContent(data.content);
        }
      }
      if (rankingsRes.ok) {
        const data = await rankingsRes.json();
        setRankings(data);
      }
      if (homeRes.ok) {
        const data = await homeRes.json();
        // Transform nested structure to flat structure
        const transformedData: HomeContent = {
          hero_title: data.hero?.title || '',
          hero_subtitle: data.hero?.subtitle || '',
          hero_image: data.hero?.image || '',
          hero_cta_text: data.hero?.cta_text || '',
          hero_cta_link: data.hero?.cta_link || '',
          section1_title: data.facilities?.title || '',
          section1_content: data.facilities?.description || ''
        };
        setHomeContent(transformedData);
      }
      if (marketingRes.ok) {
        const data: MarketingCampaignsPayload = await marketingRes.json();
        const campaigns = Array.isArray(data.campaigns) ? data.campaigns : [];
        setMarketingCampaigns(campaigns.map((campaign) => ({
          ...campaign,
          placements: normalizeMarketingPlacements(campaign.placements),
        })));
      } else {
        setMarketingCampaigns([]);
      }
    } catch (error) {
      logger.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    void loadAllData();
  }, [loadAllData]);

  // Home Management Functions
  const saveHomeContent = async () => {
    try {
      // Transform flat structure back to nested structure for API
      const apiData = {
        hero: {
          title: homeContent.hero_title,
          subtitle: homeContent.hero_subtitle,
          image: homeContent.hero_image,
          cta_text: homeContent.hero_cta_text,
          cta_link: homeContent.hero_cta_link
        },
        facilities: {
          title: homeContent.section1_title,
          description: homeContent.section1_content
        },
        services: []  // Keep empty services array for now
      };
      
      const response = await authorizedContentFetch(getContentApiUrl('home'), {
        method: 'PUT',
        body: JSON.stringify(apiData)
      }, true);
      
      if (response.ok) {
        setMessage({ type: 'success', text: t('content.messages.saved') });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('content.messages.error') });
    }
  };

  // Programme Management Functions
  const saveProgramme = async () => {
    try {
      // Auto-generate ID if not provided
      if (!programmeForm.id) {
        programmeForm.id = `programme-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }

      // Validate required fields
      if (!programmeForm.title || !programmeForm.description) {
        setMessage({ type: 'error', text: t('content.messages.requiredFields') });
        return;
      }

      const method = editingProgrammeId ? 'PUT' : 'POST';
      const url = editingProgrammeId
        ? getContentApiUrl(`programmes/${editingProgrammeId}`)
        : getContentApiUrl('programmes');

      const response = await authorizedContentFetch(url, {
        method,
        body: JSON.stringify(programmeForm)
      }, true);

      if (response.ok) {
        await loadAllData();
        setProgrammeForm({ features: [] });
        setEditingProgrammeId(null);
        setIsProgrammeModalOpen(false);
        setMessage({ type: 'success', text: t('content.messages.saved') });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('content.messages.error') });
    }
  };

  const editProgramme = (programme: Programme) => {
    setProgrammeForm(programme);
    setEditingProgrammeId(programme.id);
    setIsProgrammeModalOpen(true);
  };

  const openAddProgrammeModal = () => {
    setProgrammeForm({ features: [] });
    setEditingProgrammeId(null);
    setIsProgrammeModalOpen(true);
  };

  const closeProgrammeModal = () => {
    setIsProgrammeModalOpen(false);
    setProgrammeForm({ features: [] });
    setEditingProgrammeId(null);
  };

  const deleteProgramme = async (id: string) => {
    if (!confirm(t('content.messages.confirmDelete'))) return;
    
    try {
      const response = await authorizedContentFetch(getContentApiUrl(`programmes/${id}`), {
        method: 'DELETE'
      });
      
      if (response.ok) {
        await loadAllData();
        setMessage({ type: 'success', text: t('content.messages.deleted') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('content.messages.error') });
    }
  };

  // Article Management Functions
  const saveArticle = async () => {
    try {
      // Auto-generate ID if not provided
      if (!articleForm.id) {
        articleForm.id = `article-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }

      // Auto-generate slug if not provided
      if (!articleForm.slug && articleForm.title) {
        articleForm.slug = articleForm.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      }

      // Set date to today if not provided
      if (!articleForm.date) {
        articleForm.date = new Date().toISOString();
      }

      const method = editingArticleId ? 'PUT' : 'POST';
      const url = editingArticleId
        ? getContentApiUrl(`articles/${editingArticleId}`)
        : getContentApiUrl('articles');

      const response = await authorizedContentFetch(url, {
        method,
        body: JSON.stringify(articleForm)
      }, true);

      if (response.ok) {
        await loadAllData();
        setArticleForm({ tags: [] });
        setEditingArticleId(null);
        setIsArticleModalOpen(false);
        setMessage({ type: 'success', text: t('content.messages.saved') });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('content.messages.error') });
    }
  };

  const editArticle = (article: Article) => {
    setArticleForm(article);
    setEditingArticleId(article.id);
    setIsArticleModalOpen(true);
  };

  const openAddArticleModal = () => {
    setArticleForm({ tags: [] });
    setEditingArticleId(null);
    setIsArticleModalOpen(true);
  };

  const closeArticleModal = () => {
    setIsArticleModalOpen(false);
    setArticleForm({ tags: [] });
    setEditingArticleId(null);
  };

  const deleteArticle = async (id: string) => {
    if (!confirm(t('content.messages.confirmDelete'))) return;
    
    try {
      const response = await authorizedContentFetch(getContentApiUrl(`articles/${id}`), {
        method: 'DELETE'
      });
      
      if (response.ok) {
        await loadAllData();
        setMessage({ type: 'success', text: t('content.messages.deleted') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('content.messages.error') });
    }
  };

  // Event Management Functions
  const saveEvent = async () => {
    try {
      // Auto-generate ID if not provided
      if (!eventForm.id) {
        eventForm.id = `event-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }

      // Auto-calculate status based on date
      if (eventForm.date) {
        const eventDate = new Date(eventForm.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        eventForm.event_status = eventDate >= today ? 'UPCOMING' : 'PAST';
      }

      const method = editingEventId ? 'PUT' : 'POST';
      const url = editingEventId
        ? getContentApiUrl(`events/${editingEventId}`)
        : getContentApiUrl('events');

      const response = await authorizedContentFetch(url, {
        method,
        body: JSON.stringify(eventForm)
      }, true);

      if (response.ok) {
        await loadAllData();
        setEventForm({ categories: [], event_status: 'UPCOMING' });
        setEditingEventId(null);
        setIsEventModalOpen(false);
        setMessage({ type: 'success', text: t('content.messages.saved') });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('content.messages.error') });
    }
  };

  const editEvent = (event: EventItem) => {
    setEventForm(event);
    setEditingEventId(event.id);
    setIsEventModalOpen(true);
  };

  const openAddEventModal = () => {
    setEventForm({ categories: [], event_status: 'UPCOMING' });
    setEditingEventId(null);
    setIsEventModalOpen(true);
  };

  const closeEventModal = () => {
    setIsEventModalOpen(false);
    setEventForm({ categories: [], event_status: 'UPCOMING' });
    setEditingEventId(null);
  };

  const deleteEvent = async (id: string) => {
    if (!confirm(t('content.messages.confirmDelete'))) return;
    
    try {
      const response = await authorizedContentFetch(getContentApiUrl(`events/${id}`), {
        method: 'DELETE'
      });
      
      if (response.ok) {
        await loadAllData();
        setMessage({ type: 'success', text: t('content.messages.deleted') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('content.messages.error') });
    }
  };

  const handleEventsCSV = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await authorizedContentFetch(getContentApiUrl('events/import'), {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        await loadAllData();
        setMessage({ type: 'success', text: t('content.messages.importSuccess') });
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Import failed');
      }
    } catch (error) {
      setMessage({ type: 'error', text: `${t('content.messages.importError')}: ${error}` });
    }
  };

  // Batch delete functions for events
  const batchDeleteEvents = async () => {
    if (selectedEvents.length === 0) return;

    if (confirm(t('common.confirmBatchDelete', { count: selectedEvents.length }))) {
      try {
        const response = await authorizedContentFetch(getContentApiUrl('events/batch-delete'), {
          method: 'POST',
          body: JSON.stringify({ ids: selectedEvents }),
        }, true);

        if (response.ok) {
          const result = await response.json();
          logger.info('Batch delete result:', result);
          setSelectedEvents([]);
          await loadAllData();
          setMessage({ type: 'success', text: t('common.batchDeleteSuccess', { count: result.deleted }) });
        } else {
          throw new Error('批量删除失败');
        }
      } catch (error) {
        logger.error('Batch delete failed:', error);
        setMessage({ type: 'error', text: `批量删除失败: ${error}` });
      }
    }
  };

  const clearAllEvents = async () => {
    if (confirm(t('common.confirmClearAll'))) {
      try {
        const response = await authorizedContentFetch(getContentApiUrl('events/clear-all'), {
          method: 'POST',
        });

        if (response.ok) {
          setSelectedEvents([]);
          setEvents([]);
          setMessage({ type: 'success', text: t('common.clearAllSuccess') });
        } else {
          throw new Error('清空失败');
        }
      } catch (error) {
        logger.error('Clear all failed:', error);
        setMessage({ type: 'error', text: `清空失败: ${error}` });
      }
    }
  };

  // 同步 events 状态（将过期的活动自动标记为 PAST）
  const syncEventStatus = async () => {
    try {
      setLoading(true);
      const response = await authorizedContentFetch(getContentApiUrl('events/sync-status'), {
        method: 'POST',
      });

      if (response.ok) {
        const result = await response.json();
        // 重新加载 events 列表以显示更新后的状态
        await loadAllData();
        setMessage({ 
          type: 'success', 
          text: `已同步 ${result.updated}/${result.total} 个活动状态` 
        });
      } else {
        throw new Error('同步失败');
      }
    } catch (error) {
      logger.error('Sync event status failed:', error);
      setMessage({ type: 'error', text: `同步状态失败: ${error}` });
    } finally {
      setLoading(false);
    }
  };

  const toggleEventSelection = (eventId: string) => {
    setSelectedEvents(prev =>
      prev.includes(eventId)
        ? prev.filter(id => id !== eventId)
        : [...prev, eventId]
    );
  };

  const toggleSelectAllEvents = () => {
    setSelectedEvents(prev =>
      prev.length === events.length ? [] : events.map(e => e.id)
    );
  };

  // Coach Management Functions
  const saveCoach = async () => {
    try {
      // Auto-generate ID if not provided
      if (!coachForm.id) {
        coachForm.id = `coach-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }

      // Validate required fields
      if (!coachForm.name || !coachForm.title) {
        setMessage({ type: 'error', text: t('content.messages.requiredFields') });
        return;
      }

      const method = editingCoachId ? 'PUT' : 'POST';
      const url = editingCoachId
        ? getContentApiUrl(`coaches/${editingCoachId}`)
        : getContentApiUrl('coaches');

      const response = await authorizedContentFetch(url, {
        method,
        body: JSON.stringify(coachForm)
      }, true);

      if (response.ok) {
        await loadAllData();
        setCoachForm({ specialties: [] });
        setEditingCoachId(null);
        setIsCoachModalOpen(false);
        setMessage({ type: 'success', text: t('content.messages.saved') });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('content.messages.error') });
    }
  };

  const editCoach = (coach: Coach) => {
    setCoachForm(coach);
    setEditingCoachId(coach.id);
    setIsCoachModalOpen(true);
  };

  const openAddCoachModal = () => {
    setCoachForm({ specialties: [] });
    setEditingCoachId(null);
    setIsCoachModalOpen(true);
  };

  const closeCoachModal = () => {
    setIsCoachModalOpen(false);
    setCoachForm({ specialties: [] });
    setEditingCoachId(null);
  };

  const deleteCoach = async (id: string) => {
    if (!confirm(t('content.messages.confirmDelete'))) return;
    
    try {
      const response = await authorizedContentFetch(getContentApiUrl(`coaches/${id}`), {
        method: 'DELETE'
      });
      
      if (response.ok) {
        await loadAllData();
        setMessage({ type: 'success', text: t('content.messages.deleted') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('content.messages.error') });
    }
  };

  const saveCoachingContent = async () => {
    try {
      const response = await authorizedContentFetch(getContentApiUrl('coaching/content'), {
        method: 'PUT',
        body: JSON.stringify(coachingContent)
      }, true);
      
      if (response.ok) {
        setMessage({ type: 'success', text: t('content.messages.saved') });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('content.messages.error') });
    }
  };

  // Rankings Management Functions
  const handleRankingsCSV = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', rankingCategory);

      const response = await authorizedContentFetch(getContentApiUrl('rankings/import'), {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        await loadAllData();
        setMessage({ type: 'success', text: t('content.messages.importSuccess') });
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Import failed');
      }
    } catch (error) {
      setMessage({ type: 'error', text: `${t('content.messages.importError')}: ${error}` });
    }
  };

  const deleteRanking = async (id: string) => {
    if (!confirm(t('content.messages.confirmDelete'))) return;
    
    try {
      const response = await authorizedContentFetch(getContentApiUrl(`rankings/${id}`), {
        method: 'DELETE'
      });
      
      if (response.ok) {
        await loadAllData();
        setMessage({ type: 'success', text: t('content.messages.deleted') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('content.messages.error') });
    }
  };

  const filteredMarketingCampaigns = marketingCampaigns
    .filter(item => marketingStatusFilter === 'all' || item.status === marketingStatusFilter)
    .filter(item => marketingTypeFilter === 'all' || item.type === marketingTypeFilter)
    .sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      return String(a.startDate).localeCompare(String(b.startDate));
    });

  const updateMarketingPlacement = (key: MarketingPlacementKey, patch: Partial<MarketingPlacement>) => {
    const placements = normalizeMarketingPlacements(marketingForm.placements || []);
    const existing = placements.find(item => item.key === key);
    const next: MarketingPlacement = existing
      ? { ...existing, ...patch, key }
      : { key, enabled: Boolean(patch.enabled), order: Number(patch.order || 1) };

    const nextPlacements = existing
      ? placements.map(item => (item.key === key ? next : item))
      : [...placements, next];

    setMarketingForm({ ...marketingForm, placements: nextPlacements });
  };

  const removeMarketingPlacement = (key: MarketingPlacementKey) => {
    setMarketingForm({
      ...marketingForm,
      placements: (marketingForm.placements || []).filter(item => item.key !== key),
    });
  };

  const openAddMarketingModal = () => {
    setMarketingForm(createEmptyMarketingCampaign());
    setEditingMarketingId(null);
    setIsMarketingModalOpen(true);
  };

  const editMarketingCampaign = (campaign: MarketingCampaign) => {
    const autoCta = buildPlacementCtaByTag(campaign.content?.stageLabel?.en || campaign.type);
    const normalized: MarketingCampaign = {
      ...campaign,
      placements: normalizeMarketingPlacements(campaign.placements || []),
      content: {
        campaignName: campaign.content?.campaignName || createEmptyLocaleText(),
        tagline: campaign.content?.tagline || createEmptyLocaleText(),
        summary: campaign.content?.summary || createEmptyLocaleText(),
        stageLabel: campaign.content?.stageLabel || createEmptyLocaleText(),
        trainingFocus: campaign.content?.trainingFocus || createEmptyLocaleText(),
        scheduleText: campaign.content?.scheduleText || createEmptyLocaleText(),
      },
      cta: {
        href: campaign.cta?.href || autoCta.href,
        label: campaign.cta?.label || { en: autoCta.en, zhCN: autoCta.zhCN },
        openInNewTab: Boolean(campaign.cta?.openInNewTab),
      },
      assets: {
        gallery: {
          desktopUrl: campaign.assets?.gallery?.desktopUrl || '',
          mobileUrl: campaign.assets?.gallery?.mobileUrl || '',
          localeDesktopUrls: campaign.assets?.gallery?.localeDesktopUrls || {},
          alt: campaign.assets?.gallery?.alt || createEmptyLocaleText(),
        },
        popup: campaign.assets?.popup
          ? {
              desktopUrl: campaign.assets.popup.desktopUrl || '',
              mobileUrl: campaign.assets.popup.mobileUrl || '',
              localeDesktopUrls: campaign.assets.popup.localeDesktopUrls || {},
              alt: campaign.assets.popup.alt || createEmptyLocaleText(),
            }
          : undefined,
      },
      contactOverlay: {
        addressLine1: campaign.contactOverlay?.addressLine1 || '',
        addressLine2: campaign.contactOverlay?.addressLine2 || '',
        phone: campaign.contactOverlay?.phone || '',
        email: campaign.contactOverlay?.email || '',
      },
      popupRule: {
        enabled: Boolean(campaign.popupRule?.enabled),
        version: campaign.popupRule?.version || '',
        frequency: 'daily_once',
        timezone: 'Pacific/Auckland',
        campaignAssetKey: 'popup',
      },
    };
    setMarketingForm(normalized);
    setEditingMarketingId(campaign.id);
    setIsMarketingModalOpen(true);
  };

  const duplicateMarketingCampaign = (campaign: MarketingCampaign) => {
    const copy = {
      ...campaign,
      id: `${campaign.id}-copy-${Date.now()}`,
      slug: `${campaign.slug}-copy-${Date.now()}`,
      status: 'draft' as MarketingCampaign['status'],
      popupRule: campaign.popupRule
        ? {
            ...campaign.popupRule,
            enabled: false,
            version: '',
          }
        : undefined,
    };
    editMarketingCampaign(copy);
    setEditingMarketingId(null);
  };

  const closeMarketingModal = () => {
    setIsMarketingModalOpen(false);
    setEditingMarketingId(null);
    setMarketingForm({});
  };

  const saveMarketingCampaign = async () => {
    try {
      if (!marketingForm.id || !marketingForm.slug || !marketingForm.startDate || !marketingForm.endDate) {
        setMessage({ type: 'error', text: t('content.messages.requiredFields') });
        return;
      }
      const autoCta = buildPlacementCtaByTag(marketingForm.content?.stageLabel?.en || marketingForm.type);

      const nextCampaign: MarketingCampaign = {
        ...(createEmptyMarketingCampaign()),
        ...marketingForm,
        id: marketingForm.id,
        slug: marketingForm.slug,
        startDate: marketingForm.startDate,
        endDate: marketingForm.endDate,
        priority: Number(marketingForm.priority || 10),
        status: marketingForm.status || 'draft',
        type: marketingForm.type || 'training_camp',
        placements: normalizeMarketingPlacements(marketingForm.placements || []),
        content: {
          campaignName: marketingForm.content?.campaignName || createEmptyLocaleText(),
          tagline: marketingForm.content?.tagline || createEmptyLocaleText(),
          summary: marketingForm.content?.summary || createEmptyLocaleText(),
          stageLabel: marketingForm.content?.stageLabel || createEmptyLocaleText(),
          trainingFocus: marketingForm.content?.trainingFocus || createEmptyLocaleText(),
          scheduleText: marketingForm.content?.scheduleText || createEmptyLocaleText(),
        },
        cta: {
          href: marketingForm.cta?.href || autoCta.href,
          label: {
            en: marketingForm.cta?.label?.en || autoCta.en,
            zhCN: marketingForm.cta?.label?.zhCN || autoCta.zhCN,
          },
          openInNewTab: Boolean(marketingForm.cta?.openInNewTab),
        },
        assets: {
          gallery: {
            desktopUrl: marketingForm.assets?.gallery?.desktopUrl || '',
            mobileUrl: marketingForm.assets?.gallery?.mobileUrl || '',
            localeDesktopUrls: {
              en: marketingForm.assets?.gallery?.localeDesktopUrls?.en || '',
              zhCN: marketingForm.assets?.gallery?.localeDesktopUrls?.zhCN || '',
            },
            alt: marketingForm.assets?.gallery?.alt || createEmptyLocaleText(),
          },
          popup: marketingForm.assets?.popup?.desktopUrl
            ? {
                desktopUrl: marketingForm.assets.popup.desktopUrl,
                mobileUrl: marketingForm.assets.popup.mobileUrl || '',
                localeDesktopUrls: {
                  en: marketingForm.assets?.popup?.localeDesktopUrls?.en || '',
                  zhCN: marketingForm.assets?.popup?.localeDesktopUrls?.zhCN || '',
                },
                alt: marketingForm.assets.popup.alt || createEmptyLocaleText(),
              }
            : undefined,
        },
        contactOverlay: marketingForm.contactOverlay
          ? {
              addressLine1: marketingForm.contactOverlay.addressLine1 || '',
              addressLine2: marketingForm.contactOverlay.addressLine2 || '',
              phone: marketingForm.contactOverlay.phone || '',
              email: marketingForm.contactOverlay.email || '',
            }
          : undefined,
        popupRule: marketingForm.popupRule && marketingForm.popupRule.version
          ? {
              enabled: Boolean(marketingForm.popupRule.enabled),
              version: marketingForm.popupRule.version,
              frequency: 'daily_once',
              timezone: 'Pacific/Auckland',
              campaignAssetKey: 'popup',
            }
          : undefined,
      };

      const deduped = marketingCampaigns.filter(item => item.id !== editingMarketingId);
      const payload: MarketingCampaignsPayload = {
        campaigns: [...deduped, nextCampaign],
      };

      const response = await authorizedContentFetch(getContentApiUrl('marketing-campaigns'), {
        method: 'PUT',
        body: JSON.stringify(payload),
      }, true);

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to save marketing campaigns');
      }

      await loadAllData();
      closeMarketingModal();
      setMessage({ type: 'success', text: t('content.messages.saved') });
    } catch (error) {
      logger.error('saveMarketingCampaign failed:', error);
      setMessage({ type: 'error', text: `${t('content.messages.error')}: ${error}` });
    }
  };

  const deleteMarketingCampaign = async (id: string) => {
    if (!confirm(t('content.messages.confirmDelete'))) return;
    try {
      const payload: MarketingCampaignsPayload = {
        campaigns: marketingCampaigns.filter(item => item.id !== id),
      };
      const response = await authorizedContentFetch(getContentApiUrl('marketing-campaigns'), {
        method: 'PUT',
        body: JSON.stringify(payload),
      }, true);
      if (!response.ok) {
        throw new Error('Failed to delete marketing campaign');
      }
      await loadAllData();
      setMessage({ type: 'success', text: t('content.messages.deleted') });
    } catch (error) {
      logger.error('deleteMarketingCampaign failed:', error);
      setMessage({ type: 'error', text: t('content.messages.error') });
    }
  };

  const toggleMarketingLiveStatus = async (campaign: MarketingCampaign) => {
    const nextStatus: MarketingCampaign['status'] = campaign.status === 'live' ? 'draft' : 'live';
    try {
      const payload: MarketingCampaignsPayload = {
        campaigns: marketingCampaigns.map(item => (
          item.id === campaign.id ? { ...item, status: nextStatus } : item
        )),
      };
      const response = await authorizedContentFetch(getContentApiUrl('marketing-campaigns'), {
        method: 'PUT',
        body: JSON.stringify(payload),
      }, true);
      if (!response.ok) {
        throw new Error('Failed to update status');
      }
      await loadAllData();
      setMessage({
        type: 'success',
        text: nextStatus === 'live' ? 'Campaign is live now' : 'Campaign moved back to draft',
      });
    } catch (error) {
      logger.error('toggleMarketingLiveStatus failed:', error);
      setMessage({ type: 'error', text: t('content.messages.error') });
    }
  };

  const tabs: Array<{ key: typeof activeTab; label: string }> = [
    ...(mode === 'marketingOnly'
      ? [{ key: 'marketing' as ContentTab, label: '活动投放' }]
      : mode === 'programmeOnly'
        ? [{ key: 'programme' as ContentTab, label: t('content.tabs.programme') }]
        : [
          { key: 'home' as ContentTab, label: t('content.tabs.home') },
          { key: 'articles' as ContentTab, label: t('content.tabs.articles') },
          { key: 'events' as ContentTab, label: t('content.tabs.events') },
          { key: 'coaching' as ContentTab, label: t('content.tabs.coaching') },
          { key: 'rankings' as ContentTab, label: t('content.tabs.rankings') },
        ]),
  ];

  return (
    <div className={embedded ? 'p-0' : 'p-6'}>
      {!embedded ? (
        <h1 className="text-2xl font-bold mb-6">
          {mode === 'programmeOnly'
            ? 'Programme 内容管理'
            : mode === 'marketingOnly'
              ? '活动投放管理'
              : t('nav.contentManagement')}
        </h1>
      ) : null}
      
      {/* Message display */}
      {message && (
        <div className={`mb-4 p-4 rounded ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      {tabs.length > 1 && (
        <div className="mb-6 border-b">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div>
          {/* Home Tab */}
          {activeTab === 'home' && (
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-semibold mb-4">{t('content.home.title')}</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.home.heroTitle')}</label>
                    <input
                      type="text"
                      value={homeContent.hero_title}
                      onChange={(e) => setHomeContent({...homeContent, hero_title: e.target.value})}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.home.heroSubtitle')}</label>
                    <textarea
                      value={homeContent.hero_subtitle}
                      onChange={(e) => setHomeContent({...homeContent, hero_subtitle: e.target.value})}
                      className="w-full p-2 border rounded"
                      rows={3}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.home.heroImage')}</label>
                    <input
                      type="text"
                      value={homeContent.hero_image}
                      onChange={(e) => setHomeContent({...homeContent, hero_image: e.target.value})}
                      className="w-full p-2 border rounded"
                      placeholder="https://..."
                    />
                    {homeContent.hero_image && (
                      <img src={homeContent.hero_image} alt="Preview" className="mt-2 h-20 object-cover rounded border" />
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">{t('content.home.ctaText')}</label>
                      <input
                        type="text"
                        value={homeContent.hero_cta_text}
                        onChange={(e) => setHomeContent({...homeContent, hero_cta_text: e.target.value})}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">{t('content.home.ctaLink')}</label>
                      <input
                        type="text"
                        value={homeContent.hero_cta_link}
                        onChange={(e) => setHomeContent({...homeContent, hero_cta_link: e.target.value})}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.home.section1Title')}</label>
                    <input
                      type="text"
                      value={homeContent.section1_title}
                      onChange={(e) => setHomeContent({...homeContent, section1_title: e.target.value})}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.home.section1Content')}</label>
                    <textarea
                      value={homeContent.section1_content}
                      onChange={(e) => setHomeContent({...homeContent, section1_content: e.target.value})}
                      className="w-full p-2 border rounded"
                      rows={2}
                    />
                  </div>
                  
                  <button
                    onClick={saveHomeContent}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    {t('common.save')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Programme Tab */}
          {activeTab === 'programme' && (
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">{t('content.programme.list')}</h2>
                  <button
                    onClick={openAddProgrammeModal}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    {t('content.programme.add')}
                  </button>
                </div>
                <div className="space-y-4">
                  {programmes && programmes.length > 0 ? programmes.map(programme => (
                    <div key={programme.id} className="border p-4 rounded">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold">{programme.title}</h3>
                          {programme.subtitle && <p className="text-sm text-gray-600">{programme.subtitle}</p>}
                          <p className="text-sm mt-1">{programme.description}</p>
                          <div className="mt-2">
                            <p className="text-sm font-medium">{t('content.programme.features')}:</p>
                            <ul className="list-disc list-inside text-sm text-gray-600">
                              {programme.features && programme.features.map((feature, idx) => (
                                <li key={idx}>{feature}</li>
                              ))}
                            </ul>
                          </div>
                          {programme.callout && (
                            <p className="mt-2 text-sm italic text-green-600">{programme.callout}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => editProgramme(programme)}
                            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                          >
                            {t('common.edit')}
                          </button>
                          <button
                            onClick={() => deleteProgramme(programme.id)}
                            className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                          >
                            {t('common.delete')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <p className="text-gray-500 text-center py-4">{t('common.noData')}</p>
                  )}
                </div>
              </div>

              {/* Programme Modal */}
              <Modal
                isOpen={isProgrammeModalOpen}
                onClose={closeProgrammeModal}
                title={editingProgrammeId ? t('content.programme.edit') : t('content.programme.add')}
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.programme.icon')}</label>
                    <select
                      value={programmeForm.icon || ''}
                      onChange={(e) => setProgrammeForm({...programmeForm, icon: e.target.value as Programme['icon']})}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">{t('common.select')}</option>
                      {iconOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.programme.image')}</label>
                    <input
                      type="text"
                      value={programmeForm.image || ''}
                      onChange={(e) => setProgrammeForm({...programmeForm, image: e.target.value})}
                      className="w-full p-2 border rounded"
                      placeholder="https://..."
                    />
                    {programmeForm.image && (
                      <img src={programmeForm.image} alt="Preview" className="mt-2 h-20 object-cover rounded border" />
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.programme.title')}</label>
                    <input
                      type="text"
                      value={programmeForm.title || ''}
                      onChange={(e) => setProgrammeForm({...programmeForm, title: e.target.value})}
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.programme.subtitle')}</label>
                    <input
                      type="text"
                      value={programmeForm.subtitle || ''}
                      onChange={(e) => setProgrammeForm({...programmeForm, subtitle: e.target.value})}
                      className="w-full p-2 border rounded"
                      placeholder={t('content.programme.subtitlePlaceholder')}
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">{t('content.programme.description')}</label>
                    <textarea
                      value={programmeForm.description || ''}
                      onChange={(e) => setProgrammeForm({...programmeForm, description: e.target.value})}
                      className="w-full p-2 border rounded"
                      rows={3}
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">{t('content.programme.features')}</label>
                    <div className="space-y-2">
                      {(programmeForm.features || []).map((feature, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={feature}
                            onChange={(e) => {
                              const newFeatures = [...(programmeForm.features || [])];
                              newFeatures[index] = e.target.value;
                              setProgrammeForm({...programmeForm, features: newFeatures});
                            }}
                            className="flex-1 p-2 border rounded"
                          />
                          <button
                            onClick={() => {
                              const newFeatures = (programmeForm.features || []).filter((_, i) => i !== index);
                              setProgrammeForm({...programmeForm, features: newFeatures});
                            }}
                            className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                          >
                            {t('common.remove')}
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => setProgrammeForm({...programmeForm, features: [...(programmeForm.features || []), '']})}
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                      >
                        {t('content.programme.addFeature')}
                      </button>
                    </div>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">{t('content.programme.callout')}</label>
                    <input
                      type="text"
                      value={programmeForm.callout || ''}
                      onChange={(e) => setProgrammeForm({...programmeForm, callout: e.target.value})}
                      className="w-full p-2 border rounded"
                      placeholder={t('content.programme.calloutPlaceholder')}
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    onClick={closeProgrammeModal}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={saveProgramme}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    {t('common.save')}
                  </button>
                </div>
              </Modal>
            </div>
          )}

          {/* Articles Tab */}
          {activeTab === 'articles' && (
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">{t('content.articles.list')}</h2>
                  <button
                    onClick={openAddArticleModal}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    {t('content.articles.add')}
                  </button>
                </div>
                <div className="space-y-4">
                  {articles && articles.length > 0 ? articles.map(article => (
                    <div key={article.id} className="border p-4 rounded">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold">{article.title}</h3>
                          <p className="text-sm text-gray-600">{new Date(article.date).toLocaleDateString()}</p>
                          <p className="text-sm mt-1">{article.excerpt}</p>
                          <div className="mt-2 flex gap-1">
                            {article.tags && article.tags.map(tag => (
                              <span key={tag} className="px-2 py-1 bg-gray-100 text-xs rounded">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => editArticle(article)}
                            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                          >
                            {t('common.edit')}
                          </button>
                          <button
                            onClick={() => deleteArticle(article.id)}
                            className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                          >
                            {t('common.delete')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <p className="text-gray-500 text-center py-4">{t('common.noData')}</p>
                  )}
                </div>
              </div>

              {/* Article Modal */}
              <Modal
                isOpen={isArticleModalOpen}
                onClose={closeArticleModal}
                title={editingArticleId ? t('content.articles.edit') : t('content.articles.add')}
                size="xl"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.articles.title')}</label>
                    <input
                      type="text"
                      value={articleForm.title || ''}
                      onChange={(e) => setArticleForm({...articleForm, title: e.target.value})}
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.articles.slug')}</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={articleForm.slug || (articleForm.title ? articleForm.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : '')}
                        onChange={(e) => setArticleForm({...articleForm, slug: e.target.value})}
                        className="w-full p-2 border rounded pr-10"
                        placeholder={t('content.articles.slugPlaceholder')}
                      />
                      {articleForm.title && !articleForm.slug && (
                        <span className="absolute right-2 top-2 text-xs text-gray-400">{t('content.articles.autoGenerated')}</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.articles.date')}</label>
                    <input
                      type="date"
                      value={articleForm.date ? articleForm.date.split('T')[0] : ''}
                      onChange={(e) => setArticleForm({...articleForm, date: e.target.value})}
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.articles.featuredImage')}</label>
                    <input
                      type="text"
                      value={articleForm.featuredImage?.sourceUrl || ''}
                      onChange={(e) => setArticleForm({...articleForm, featuredImage: { sourceUrl: e.target.value }})}
                      className="w-full p-2 border rounded"
                      placeholder="https://..."
                    />
                    {articleForm.featuredImage?.sourceUrl && (
                      <img src={articleForm.featuredImage.sourceUrl} alt="Preview" className="mt-2 h-20 object-cover rounded border" />
                    )}
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">{t('content.articles.tags')}</label>
                    <input
                      type="text"
                      value={(articleForm.tags || []).join(', ')}
                      onChange={(e) => setArticleForm({...articleForm, tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)})}
                      className="w-full p-2 border rounded"
                      placeholder={t('content.articles.tagsPlaceholder')}
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">{t('content.articles.excerpt')}</label>
                    <textarea
                      value={articleForm.excerpt || ''}
                      onChange={(e) => setArticleForm({...articleForm, excerpt: e.target.value})}
                      className="w-full p-2 border rounded"
                      rows={2}
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">{t('content.articles.content')}</label>
                    <textarea
                      value={articleForm.content || ''}
                      onChange={(e) => setArticleForm({...articleForm, content: e.target.value})}
                      className="w-full p-2 border rounded"
                      rows={10}
                      placeholder={t('content.articles.contentPlaceholder')}
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    onClick={closeArticleModal}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={saveArticle}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    {t('common.save')}
                  </button>
                </div>
              </Modal>
            </div>
          )}

          {/* Events Tab */}
          {activeTab === 'events' && (
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-semibold mb-4">{t('content.events.importCSV')}</h2>
                <CSVUploader
                  onUpload={handleEventsCSV}
                  templateHeaders={['event_name', 'start_date', 'categories', 'event_level', 'event_status']}
                />
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">{t('content.events.list')}</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={syncEventStatus}
                      disabled={loading}
                      className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                      title="将过期的活动自动标记为 PAST"
                    >
                      {loading ? '同步中...' : '同步状态'}
                    </button>
                    <button
                      onClick={openAddEventModal}
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      {t('content.events.add')}
                    </button>
                  </div>
                </div>

                {/* Batch Operation Bar */}
                {events && events.length > 0 && (
                  <div className="flex justify-between items-center mb-4 p-3 bg-gray-50 rounded">
                    <div className="flex items-center gap-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedEvents.length === events.length}
                          onChange={toggleSelectAllEvents}
                          className="mr-2"
                        />
{t('common.selectAll')} ({selectedEvents.length}/{events.length})
                      </label>
                    </div>
                    <div className="flex gap-2">
                      {selectedEvents.length > 0 && (
                        <button
                          onClick={batchDeleteEvents}
                          className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                        >
{t('common.deleteSelected')} ({selectedEvents.length})
                        </button>
                      )}
                      <button
                        onClick={clearAllEvents}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                      >
{t('common.clearAll')}
                      </button>
                    </div>
                  </div>
                )}
                <div className="space-y-4">
                  {events && events.length > 0 ? events.map(event => (
                    <div key={event.id} className="border p-4 rounded">
                      <div className="flex justify-between items-start">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={selectedEvents.includes(event.id)}
                            onChange={() => toggleEventSelection(event.id)}
                            className="mt-1"
                          />
                          <div>
                          <h3 className="font-semibold">{event.title}</h3>
                          <p className="text-sm text-gray-600">
                            {new Date(event.date).toLocaleDateString()} - {event.location}
                          </p>
                          {event.event_level && (
                            <span className="inline-block mt-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                              {event.event_level}
                            </span>
                          )}
                          <span className={`inline-block mt-1 ml-2 px-2 py-1 text-xs rounded ${
                            event.event_status === 'UPCOMING'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {event.event_status}
                          </span>
                          {event.categories && event.categories.length > 0 && (
                            <div className="mt-2 flex gap-1">
                              {event.categories.map(cat => (
                                <span key={cat} className="px-2 py-1 bg-gray-100 text-xs rounded">
                                  {cat}
                                </span>
                              ))}
                            </div>
                          )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => editEvent(event)}
                            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                          >
                            {t('common.edit')}
                          </button>
                          <button
                            onClick={() => deleteEvent(event.id)}
                            className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                          >
                            {t('common.delete')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <p className="text-gray-500 text-center py-4">{t('common.noData')}</p>
                  )}
                </div>
              </div>

              {/* Event Modal */}
              <Modal
                isOpen={isEventModalOpen}
                onClose={closeEventModal}
                title={editingEventId ? t('content.events.edit') : t('content.events.add')}
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.events.title')}</label>
                    <input
                      type="text"
                      value={eventForm.title || ''}
                      onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.events.date')}</label>
                    <input
                      type="date"
                      value={eventForm.date ? eventForm.date.split('T')[0] : ''}
                      onChange={(e) => setEventForm({...eventForm, date: e.target.value})}
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">End Date</label>
                    <input
                      type="date"
                      value={eventForm.end_date ? eventForm.end_date.split('T')[0] : ''}
                      onChange={(e) => setEventForm({...eventForm, end_date: e.target.value})}
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.events.location')}</label>
                    <input
                      type="text"
                      value={eventForm.location || ''}
                      onChange={(e) => setEventForm({...eventForm, location: e.target.value})}
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.events.level')}</label>
                    <select
                      value={eventForm.event_level || ''}
                      onChange={(e) => setEventForm({...eventForm, event_level: e.target.value})}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">{t('common.select')}</option>
                      <option value="BWF_TOUR">BWF Tour</option>
                      <option value="NATIONAL">National</option>
                      <option value="REGIONAL">Regional</option>
                      <option value="CLUB">Club</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.events.status')}</label>
                    <div className="p-2 border rounded bg-gray-50">
                      <span className={`inline-block px-3 py-1 rounded text-sm font-medium ${
                        eventForm.date && new Date(eventForm.date) >= new Date(new Date().setHours(0,0,0,0))
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {eventForm.date
                          ? (new Date(eventForm.date) >= new Date(new Date().setHours(0,0,0,0))
                            ? t('content.events.statusUpcoming')
                            : t('content.events.statusPast'))
                          : t('content.events.selectDate')
                        }
                      </span>
                      <p className="text-xs text-gray-500 mt-1">{t('content.events.autoStatus')}</p>
                    </div>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">{t('content.events.link')}</label>
                    <input
                      type="text"
                      value={eventForm.link || ''}
                      onChange={(e) => setEventForm({...eventForm, link: e.target.value})}
                      className="w-full p-2 border rounded"
                      placeholder="https://..."
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Image URL</label>
                    <input
                      type="text"
                      value={eventForm.image_url || ''}
                      onChange={(e) => setEventForm({...eventForm, image_url: e.target.value})}
                      className="w-full p-2 border rounded"
                      placeholder="https://nexcave.s3.amazonaws.com/Events/..."
                    />
                    {eventForm.image_url && (
                      <div className="mt-2">
                        <img
                          src={eventForm.image_url}
                          alt="Event preview"
                          className="h-20 w-auto rounded border"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">{t('content.events.categories')}</label>
                    <input
                      type="text"
                      value={(eventForm.categories || []).join(', ')}
                      onChange={(e) => setEventForm({...eventForm, categories: e.target.value.split(',').map(c => c.trim()).filter(c => c)})}
                      className="w-full p-2 border rounded"
                      placeholder={t('content.events.categoriesPlaceholder')}
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    onClick={closeEventModal}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={saveEvent}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    {t('common.save')}
                  </button>
                </div>
              </Modal>
            </div>
          )}

          {/* Coaching Tab */}
          {activeTab === 'coaching' && (
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">{t('content.coaching.coachesList')}</h2>
                  <button
                    onClick={openAddCoachModal}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    {t('content.coaching.addCoach')}
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {coaches && coaches.map(coach => (
                    <div key={coach.id} className="border p-4 rounded">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold">{coach.name}</h3>
                          <p className="text-sm text-gray-600">{coach.title}</p>
                          <p className="text-sm mt-1">{coach.bio}</p>
                          <p className="text-sm mt-2">
                            <span className="font-medium">{t('content.coaching.experience')}:</span> {coach.experience} |
                            <span className="font-medium ml-2">{t('content.coaching.rate')}:</span> ${coach.hourlyRate}/hr
                          </p>
                          <div className="mt-2 flex gap-1">
                            {coach.specialties && coach.specialties.map(specialty => (
                              <span key={specialty} className="px-2 py-1 bg-gray-100 text-xs rounded">
                                {specialty}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => editCoach(coach)}
                            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                          >
                            {t('common.edit')}
                          </button>
                          <button
                            onClick={() => deleteCoach(coach.id)}
                            className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                          >
                            {t('common.delete')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Coach Modal */}
              <Modal
                isOpen={isCoachModalOpen}
                onClose={closeCoachModal}
                title={editingCoachId ? t('content.coaching.editCoach') : t('content.coaching.addCoach')}
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.coaching.name')}</label>
                    <input
                      type="text"
                      value={coachForm.name || ''}
                      onChange={(e) => setCoachForm({...coachForm, name: e.target.value})}
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.coaching.title')}</label>
                    <input
                      type="text"
                      value={coachForm.title || ''}
                      onChange={(e) => setCoachForm({...coachForm, title: e.target.value})}
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.coaching.experience')}</label>
                    <input
                      type="text"
                      value={coachForm.experience || ''}
                      onChange={(e) => setCoachForm({...coachForm, experience: e.target.value})}
                      className="w-full p-2 border rounded"
                      placeholder="10 years"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.coaching.hourlyRate')}</label>
                    <input
                      type="number"
                      value={coachForm.hourlyRate || ''}
                      onChange={(e) => setCoachForm({...coachForm, hourlyRate: Number(e.target.value)})}
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.coaching.email')}</label>
                    <input
                      type="email"
                      value={coachForm.email || ''}
                      onChange={(e) => setCoachForm({...coachForm, email: e.target.value})}
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.coaching.image')}</label>
                    <input
                      type="text"
                      value={coachForm.image || ''}
                      onChange={(e) => setCoachForm({...coachForm, image: e.target.value})}
                      className="w-full p-2 border rounded"
                      placeholder="https://..."
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">{t('content.coaching.bio')}</label>
                    <textarea
                      value={coachForm.bio || ''}
                      onChange={(e) => setCoachForm({...coachForm, bio: e.target.value})}
                      className="w-full p-2 border rounded"
                      rows={3}
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">{t('content.coaching.specialties')}</label>
                    <input
                      type="text"
                      value={(coachForm.specialties || []).join(', ')}
                      onChange={(e) => setCoachForm({...coachForm, specialties: e.target.value.split(',').map(s => s.trim()).filter(s => s)})}
                      className="w-full p-2 border rounded"
                      placeholder={t('content.coaching.specialtiesPlaceholder')}
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    onClick={closeCoachModal}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={saveCoach}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    {t('common.save')}
                  </button>
                </div>
              </Modal>

              <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-semibold mb-4">{t('content.coaching.pageContent')}</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.coaching.pageTitle')}</label>
                    <input
                      type="text"
                      value={coachingContent.page_title}
                      onChange={(e) => setCoachingContent({...coachingContent, page_title: e.target.value})}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.coaching.pageSubtitle')}</label>
                    <input
                      type="text"
                      value={coachingContent.page_subtitle}
                      onChange={(e) => setCoachingContent({...coachingContent, page_subtitle: e.target.value})}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.coaching.philosophyTitle')}</label>
                    <input
                      type="text"
                      value={coachingContent.philosophy_title}
                      onChange={(e) => setCoachingContent({...coachingContent, philosophy_title: e.target.value})}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('content.coaching.philosophyContent')}</label>
                    <textarea
                      value={coachingContent.philosophy_content ? coachingContent.philosophy_content.join('\n\n') : ''}
                      onChange={(e) => setCoachingContent({...coachingContent, philosophy_content: e.target.value.split('\n\n').filter(p => p.trim())})}
                      className="w-full p-2 border rounded"
                      rows={4}
                      placeholder={t('content.coaching.philosophyPlaceholder')}
                    />
                  </div>
                </div>
                
                <button
                  onClick={saveCoachingContent}
                  className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          )}

          {/* Marketing Tab */}
          {activeTab === 'marketing' && (
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-end">
                  <div>
                    <h2 className="text-xl font-semibold">Marketing Campaigns</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Manage fixed promotion slots for homepage gallery and popup.
                    </p>
                  </div>
                  <button
                    onClick={openAddMarketingModal}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Add Campaign
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Status Filter</label>
                    <select
                      value={marketingStatusFilter}
                      onChange={(e) => setMarketingStatusFilter(e.target.value as typeof marketingStatusFilter)}
                      className="w-full p-2 border rounded"
                    >
                      <option value="all">All</option>
                      <option value="draft">Draft</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="live">Live</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Type Filter</label>
                    <select
                      value={marketingTypeFilter}
                      onChange={(e) => setMarketingTypeFilter(e.target.value as typeof marketingTypeFilter)}
                      className="w-full p-2 border rounded"
                    >
                      <option value="all">All</option>
                      <option value="training_camp">Training Camp</option>
                      <option value="event">Event</option>
                      <option value="promotion">Promotion</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {filteredMarketingCampaigns.length > 0 ? filteredMarketingCampaigns.map((campaign) => (
                  <div key={campaign.id} className="bg-white rounded-lg shadow border p-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:justify-between">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold">{campaign.content.campaignName.en || campaign.id}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded ${
                            campaign.status === 'live' ? 'bg-green-100 text-green-700' :
                            campaign.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                            campaign.status === 'archived' ? 'bg-gray-200 text-gray-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {campaign.status}
                          </span>
                          <span className="px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-700">
                            {campaign.type}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {campaign.startDate} ~ {campaign.endDate} · Priority {campaign.priority}
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          Placements: {campaign.placements.filter(item => item.enabled).map(item => `${item.key}(#${item.order})`).join(', ') || 'None'}
                        </p>
                        {campaign.popupRule?.enabled ? (
                          <p className="text-xs text-blue-700 mt-1">
                            Popup version: {campaign.popupRule.version}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => toggleMarketingLiveStatus(campaign)}
                          className={`px-3 py-1.5 text-white rounded ${
                            campaign.status === 'live' ? 'bg-slate-500 hover:bg-slate-600' : 'bg-green-500 hover:bg-green-600'
                          }`}
                        >
                          {campaign.status === 'live' ? 'Set Draft' : 'Go Live'}
                        </button>
                        <button
                          onClick={() => editMarketingCampaign(campaign)}
                          className="px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => duplicateMarketingCampaign(campaign)}
                          className="px-3 py-1.5 bg-indigo-500 text-white rounded hover:bg-indigo-600"
                        >
                          Duplicate
                        </button>
                        <button
                          onClick={() => deleteMarketingCampaign(campaign.id)}
                          className="px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="bg-white rounded-lg shadow border p-6 text-center text-gray-500">
                    No marketing campaigns.
                  </div>
                )}
              </div>

              <Modal
                isOpen={isMarketingModalOpen}
                onClose={closeMarketingModal}
                title={editingMarketingId ? 'Edit Marketing Campaign' : 'Add Marketing Campaign'}
                size="xl"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Campaign ID</label>
                    <input
                      type="text"
                      value={marketingForm.id || ''}
                      onChange={(e) => setMarketingForm({ ...marketingForm, id: e.target.value })}
                      className="w-full p-2 border rounded"
                      placeholder="summer-camp-phase-1-2026"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Slug</label>
                    <input
                      type="text"
                      value={marketingForm.slug || ''}
                      onChange={(e) => setMarketingForm({ ...marketingForm, slug: e.target.value })}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Type</label>
                    <select
                      value={marketingForm.type || 'training_camp'}
                      onChange={(e) => setMarketingForm({ ...marketingForm, type: e.target.value as MarketingCampaign['type'] })}
                      className="w-full p-2 border rounded"
                    >
                      <option value="training_camp">training_camp</option>
                      <option value="event">event</option>
                      <option value="promotion">promotion</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Status</label>
                    <select
                      value={marketingForm.status || 'draft'}
                      onChange={(e) => setMarketingForm({ ...marketingForm, status: e.target.value as MarketingCampaign['status'] })}
                      className="w-full p-2 border rounded"
                    >
                      <option value="draft">draft</option>
                      <option value="scheduled">scheduled</option>
                      <option value="live">live</option>
                      <option value="archived">archived</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Start Date</label>
                    <input
                      type="date"
                      value={marketingForm.startDate || ''}
                      onChange={(e) => setMarketingForm({ ...marketingForm, startDate: e.target.value })}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">End Date</label>
                    <input
                      type="date"
                      value={marketingForm.endDate || ''}
                      onChange={(e) => setMarketingForm({ ...marketingForm, endDate: e.target.value })}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Priority (lower is higher)</label>
                    <input
                      type="number"
                      value={marketingForm.priority || 10}
                      onChange={(e) => setMarketingForm({ ...marketingForm, priority: Number(e.target.value) })}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Audience Tag</label>
                    <input
                      type="text"
                      value={marketingForm.content?.stageLabel?.en || ''}
                      onChange={(e) => {
                        const nextContent = ensureMarketingContent(marketingForm.content);
                        nextContent.stageLabel = {
                          ...(nextContent.stageLabel || createEmptyLocaleText()),
                          en: e.target.value,
                        };
                        setMarketingForm({
                          ...marketingForm,
                          content: nextContent,
                        });
                      }}
                      className="w-full p-2 border rounded"
                      placeholder="beginner / private / junior"
                    />
                  </div>

                  <div className="md:col-span-2 border rounded p-3 bg-slate-50">
                    <p className="text-sm font-semibold mb-2">Localized Content</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={marketingForm.content?.campaignName?.en || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          content: {
                            ...(marketingForm.content || {}),
                            campaignName: {
                              ...(marketingForm.content?.campaignName || createEmptyLocaleText()),
                              en: e.target.value,
                            }
                          } as MarketingCampaign['content'],
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="Campaign name (EN)"
                      />
                      <input
                        type="text"
                        value={marketingForm.content?.campaignName?.zhCN || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          content: {
                            ...(marketingForm.content || {}),
                            campaignName: {
                              ...(marketingForm.content?.campaignName || createEmptyLocaleText()),
                              zhCN: e.target.value,
                            }
                          } as MarketingCampaign['content'],
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="Campaign name (ZH)"
                      />
                      <input
                        type="text"
                        value={marketingForm.content?.summary?.en || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          content: {
                            ...(marketingForm.content || {}),
                            summary: {
                              ...(marketingForm.content?.summary || createEmptyLocaleText()),
                              en: e.target.value,
                            }
                          } as MarketingCampaign['content'],
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="Summary (EN)"
                      />
                      <input
                        type="text"
                        value={marketingForm.content?.summary?.zhCN || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          content: {
                            ...(marketingForm.content || {}),
                            summary: {
                              ...(marketingForm.content?.summary || createEmptyLocaleText()),
                              zhCN: e.target.value,
                            }
                          } as MarketingCampaign['content'],
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="Summary (ZH)"
                      />
                      <input
                        type="text"
                        value={marketingForm.content?.tagline?.en || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          content: {
                            ...(marketingForm.content || {}),
                            tagline: {
                              ...(marketingForm.content?.tagline || createEmptyLocaleText()),
                              en: e.target.value,
                            }
                          } as MarketingCampaign['content'],
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="Tagline (EN)"
                      />
                      <input
                        type="text"
                        value={marketingForm.content?.tagline?.zhCN || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          content: {
                            ...(marketingForm.content || {}),
                            tagline: {
                              ...(marketingForm.content?.tagline || createEmptyLocaleText()),
                              zhCN: e.target.value,
                            }
                          } as MarketingCampaign['content'],
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="Tagline (ZH)"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2 border rounded p-3 bg-slate-50">
                    <p className="text-sm font-semibold mb-2">Assets</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={marketingForm.assets?.gallery?.desktopUrl || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          assets: {
                            ...(marketingForm.assets || { gallery: { desktopUrl: '', alt: createEmptyLocaleText() } }),
                            gallery: {
                              ...(marketingForm.assets?.gallery || { desktopUrl: '', alt: createEmptyLocaleText() }),
                              desktopUrl: e.target.value,
                            }
                          }
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="Gallery desktop URL"
                      />
                      <input
                        type="text"
                        value={marketingForm.assets?.popup?.desktopUrl || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          assets: {
                            ...(marketingForm.assets || { gallery: { desktopUrl: '', alt: createEmptyLocaleText() } }),
                            popup: {
                              ...(marketingForm.assets?.popup || { desktopUrl: '', alt: createEmptyLocaleText() }),
                              desktopUrl: e.target.value,
                            }
                          }
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="Popup desktop URL (optional)"
                      />
                      <input
                        type="text"
                        value={marketingForm.assets?.popup?.localeDesktopUrls?.en || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          assets: {
                            ...(marketingForm.assets || { gallery: { desktopUrl: '', alt: createEmptyLocaleText() } }),
                            popup: {
                              ...(marketingForm.assets?.popup || { desktopUrl: '', alt: createEmptyLocaleText() }),
                              localeDesktopUrls: {
                                ...(marketingForm.assets?.popup?.localeDesktopUrls || {}),
                                en: e.target.value,
                              }
                            }
                          }
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="Popup EN desktop URL (optional)"
                      />
                      <input
                        type="text"
                        value={marketingForm.assets?.popup?.localeDesktopUrls?.zhCN || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          assets: {
                            ...(marketingForm.assets || { gallery: { desktopUrl: '', alt: createEmptyLocaleText() } }),
                            popup: {
                              ...(marketingForm.assets?.popup || { desktopUrl: '', alt: createEmptyLocaleText() }),
                              localeDesktopUrls: {
                                ...(marketingForm.assets?.popup?.localeDesktopUrls || {}),
                                zhCN: e.target.value,
                              }
                            }
                          }
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="Popup ZH desktop URL (optional)"
                      />
                      <input
                        type="text"
                        value={marketingForm.assets?.gallery?.alt?.en || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          assets: {
                            ...(marketingForm.assets || { gallery: { desktopUrl: '', alt: createEmptyLocaleText() } }),
                            gallery: {
                              ...(marketingForm.assets?.gallery || { desktopUrl: '', alt: createEmptyLocaleText() }),
                              alt: {
                                ...(marketingForm.assets?.gallery?.alt || createEmptyLocaleText()),
                                en: e.target.value,
                              },
                            }
                          }
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="Gallery alt (EN)"
                      />
                      <input
                        type="text"
                        value={marketingForm.assets?.gallery?.alt?.zhCN || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          assets: {
                            ...(marketingForm.assets || { gallery: { desktopUrl: '', alt: createEmptyLocaleText() } }),
                            gallery: {
                              ...(marketingForm.assets?.gallery || { desktopUrl: '', alt: createEmptyLocaleText() }),
                              alt: {
                                ...(marketingForm.assets?.gallery?.alt || createEmptyLocaleText()),
                                zhCN: e.target.value,
                              },
                            }
                          }
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="Gallery alt (ZH)"
                      />
                      <input
                        type="text"
                        value={marketingForm.assets?.popup?.alt?.en || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          assets: {
                            ...(marketingForm.assets || { gallery: { desktopUrl: '', alt: createEmptyLocaleText() } }),
                            popup: {
                              ...(marketingForm.assets?.popup || { desktopUrl: '', alt: createEmptyLocaleText() }),
                              alt: {
                                ...(marketingForm.assets?.popup?.alt || createEmptyLocaleText()),
                                en: e.target.value,
                              }
                            }
                          }
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="Popup alt (EN)"
                      />
                      <input
                        type="text"
                        value={marketingForm.assets?.popup?.alt?.zhCN || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          assets: {
                            ...(marketingForm.assets || { gallery: { desktopUrl: '', alt: createEmptyLocaleText() } }),
                            popup: {
                              ...(marketingForm.assets?.popup || { desktopUrl: '', alt: createEmptyLocaleText() }),
                              alt: {
                                ...(marketingForm.assets?.popup?.alt || createEmptyLocaleText()),
                                zhCN: e.target.value,
                              }
                            }
                          }
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="Popup alt (ZH)"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2 border rounded p-3 bg-slate-50">
                    <p className="text-sm font-semibold mb-2">CTA + Contact Overlay</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={marketingForm.cta?.href || '/programme'}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          cta: { ...(marketingForm.cta || { href: '/programme', label: createEmptyLocaleText() }), href: e.target.value }
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="CTA href"
                      />
                      <label className="flex items-center gap-2 text-sm p-2 border rounded">
                        <input
                          type="checkbox"
                          checked={Boolean(marketingForm.cta?.openInNewTab)}
                          onChange={(e) => setMarketingForm({
                            ...marketingForm,
                            cta: {
                              ...(marketingForm.cta || { href: '/programme', label: createEmptyLocaleText() }),
                              openInNewTab: e.target.checked,
                            }
                          })}
                        />
                        Open CTA in new tab
                      </label>
                      <input
                        type="text"
                        value={marketingForm.cta?.label?.en || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          cta: {
                            ...(marketingForm.cta || { href: '/programme', label: createEmptyLocaleText() }),
                            label: {
                              ...(marketingForm.cta?.label || createEmptyLocaleText()),
                              en: e.target.value,
                            }
                          }
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="CTA label (EN)"
                      />
                      <input
                        type="text"
                        value={marketingForm.cta?.label?.zhCN || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          cta: {
                            ...(marketingForm.cta || { href: '/programme', label: createEmptyLocaleText() }),
                            label: {
                              ...(marketingForm.cta?.label || createEmptyLocaleText()),
                              zhCN: e.target.value,
                            }
                          }
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="CTA label (ZH)"
                      />
                      <input
                        type="text"
                        value={marketingForm.contactOverlay?.addressLine1 || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          contactOverlay: {
                            ...(marketingForm.contactOverlay || { addressLine1: '', addressLine2: '', phone: '', email: '' }),
                            addressLine1: e.target.value,
                          }
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="Address line 1"
                      />
                      <input
                        type="text"
                        value={marketingForm.contactOverlay?.addressLine2 || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          contactOverlay: {
                            ...(marketingForm.contactOverlay || { addressLine1: '', addressLine2: '', phone: '', email: '' }),
                            addressLine2: e.target.value,
                          }
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="Address line 2"
                      />
                      <input
                        type="text"
                        value={marketingForm.contactOverlay?.phone || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          contactOverlay: {
                            ...(marketingForm.contactOverlay || { addressLine1: '', addressLine2: '', phone: '', email: '' }),
                            phone: e.target.value,
                          }
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="Phone"
                      />
                      <input
                        type="email"
                        value={marketingForm.contactOverlay?.email || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          contactOverlay: {
                            ...(marketingForm.contactOverlay || { addressLine1: '', addressLine2: '', phone: '', email: '' }),
                            email: e.target.value,
                          }
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="Email (optional)"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2 border rounded p-3 bg-slate-50">
                    <p className="text-sm font-semibold mb-2">Placements</p>
                    <div className="space-y-2">
                      {marketingPlacementOptions.map((placementKey) => {
                        const placement = (marketingForm.placements || []).find(item => item.key === placementKey);
                        return (
                          <div key={placementKey} className="grid grid-cols-1 md:grid-cols-[180px_1fr_auto_auto] gap-2 items-center">
                            <span className="text-sm font-medium">{placementKey}</span>
                            <input
                              type="number"
                              min={1}
                              value={placement?.order || 1}
                              onChange={(e) => updateMarketingPlacement(placementKey, { order: Number(e.target.value) || 1 })}
                              className="p-2 border rounded"
                              placeholder="Order"
                            />
                            <label className="text-sm flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={Boolean(placement?.enabled)}
                                onChange={(e) => updateMarketingPlacement(placementKey, { enabled: e.target.checked })}
                              />
                              Enabled
                            </label>
                            <button
                              type="button"
                              onClick={() => removeMarketingPlacement(placementKey)}
                              className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                            >
                              Remove
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="md:col-span-2 border rounded p-3 bg-slate-50">
                    <p className="text-sm font-semibold mb-2">Popup Rule (optional)</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <label className="flex items-center gap-2 text-sm p-2 border rounded">
                        <input
                          type="checkbox"
                          checked={Boolean(marketingForm.popupRule?.enabled)}
                          onChange={(e) => setMarketingForm({
                            ...marketingForm,
                            popupRule: {
                              enabled: e.target.checked,
                              version: marketingForm.popupRule?.version || '',
                              frequency: 'daily_once',
                              timezone: 'Pacific/Auckland',
                              campaignAssetKey: 'popup',
                            }
                          })}
                        />
                        Popup Enabled
                      </label>
                      <input
                        type="text"
                        value={marketingForm.popupRule?.version || ''}
                        onChange={(e) => setMarketingForm({
                          ...marketingForm,
                          popupRule: {
                            enabled: Boolean(marketingForm.popupRule?.enabled),
                            version: e.target.value,
                            frequency: 'daily_once',
                            timezone: 'Pacific/Auckland',
                            campaignAssetKey: 'popup',
                          }
                        })}
                        className="w-full p-2 border rounded"
                        placeholder="popup version, e.g. 2026-summer-camp-v1"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    onClick={closeMarketingModal}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={saveMarketingCampaign}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    {t('common.save')}
                  </button>
                </div>
              </Modal>
            </div>
          )}

          {/* Rankings Tab */}
          {activeTab === 'rankings' && (
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-semibold mb-4">{t('content.rankings.importCSV')}</h2>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">{t('content.rankings.category')}</label>
                  <select
                    value={rankingCategory}
                    onChange={(e) => setRankingCategory(e.target.value)}
                    className="w-full p-2 border rounded"
                  >
                    <option value="Men's Singles">{t('content.rankings.categories.mensSingles')}</option>
                    <option value="Women's Singles">{t('content.rankings.categories.womensSingles')}</option>
                    <option value="Men's Doubles">{t('content.rankings.categories.mensDoubles')}</option>
                    <option value="Women's Doubles">{t('content.rankings.categories.womensDoubles')}</option>
                    <option value="Mixed Doubles">{t('content.rankings.categories.mixedDoubles')}</option>
                  </select>
                </div>
                <CSVUploader
                  onUpload={handleRankingsCSV}
                  templateHeaders={['player_name', 'ranking', 'points', 'country', 'change']}
                />
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-semibold mb-4">{t('content.rankings.list')}</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('content.rankings.rank')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('content.rankings.player')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('content.rankings.country')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('content.rankings.points')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('content.rankings.change')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('common.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {rankings.filter(r => r.category === rankingCategory).map(ranking => (
                        <tr key={ranking.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {ranking.ranking}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {ranking.player_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {ranking.country}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {ranking.points}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {ranking.change ? (
                              <span className={ranking.change > 0 ? 'text-green-600' : ranking.change < 0 ? 'text-red-600' : ''}>
                                {ranking.change > 0 ? `↑${ranking.change}` : ranking.change < 0 ? `↓${Math.abs(ranking.change)}` : '-'}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => deleteRanking(ranking.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              {t('common.delete')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContentManagement;
